import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { settingsQueries } from '../../lib/queries';
import { sans } from '../Vendors/VendorDetails';
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorBlock, Field, LoadingBlock, Modal, Notice, Palette, Panel,
  ReadOnlyHint, SectionHeader, SettingsSectionProps, Spinner, Toggle, apiError, btn, fmtDate, inputStyle, palette,
  selectStyle, timeAgo,
} from './settingsUi';

/**
 * Settings -> AI providers (BYOK). A tenant connects its own key for any provider in the backend catalog; the
 * keys are encrypted at rest and power AI summaries, indicator descriptions and company profiles. AI never
 * changes a risk score. Every mutating route here is admin-only on the server, so non-admins get a read-only list.
 */

interface ExtraField {
  key: string;
  label: string;
  placeholder: string;
  secret: boolean;
}

interface CatalogEntry {
  label: string;
  litellm_prefix: string;
  models: string[];
  key_hint: string;
  allow_custom_model?: boolean;
  model_help?: string;
  key_optional?: boolean;
  extra_fields?: ExtraField[];
}

interface AIProviderKey {
  id: string;
  provider_name: string;
  default_model: string | null;
  // The API returns these as 0/1 integers straight from Postgres; treat them as truthy/falsy.
  is_active: boolean | number;
  last_tested_at: string | null;
  last_test_success: boolean | number | null;
  created_at: string;
  updated_at?: string | null;
  api_key_masked: string | null;
}

type ConfigBody = { provider_name: string; api_key: string; default_model: string; extra_config: Record<string, string> | null };

// Brand colour per provider for the small initials mark. Any other provider in the catalog gets the accent colour.
const PROVIDER_COLORS: Record<string, string> = {
  openai: '#10a37f', anthropic: '#d97706', gemini: '#4285F4', deepseek: '#635bff', mistral: '#fa5210', groq: '#f55036',
  xai: '#000000', openrouter: '#6467f2', cohere: '#39594d', together_ai: '#0f6fff', fireworks_ai: '#e0439d',
  perplexity: '#1fb8cd', deepinfra: '#5b21b6', cerebras: '#f7941d', azure: '#0078d4', bedrock: '#ff9900',
  vertex_ai: '#4285F4', ollama: '#000000',
};
const PROVIDER_ORDER = [
  'deepseek', 'openai', 'anthropic', 'gemini', 'mistral', 'groq', 'xai', 'openrouter',
  'cohere', 'together_ai', 'fireworks_ai', 'perplexity', 'deepinfra', 'cerebras',
  'azure', 'bedrock', 'vertex_ai', 'ollama',
];

const EMPTY_CATALOG: Record<string, CatalogEntry> = {};
const EMPTY_PROVIDERS: AIProviderKey[] = [];

// Debounce delay (ms) before auto-fetching live models after the user stops typing an API key.
const MODEL_FETCH_DEBOUNCE_MS = 700;

type DialogState = null | { step: 'pick' } | { step: 'configure'; provider: string; fromPicker: boolean };

// The real provider's own domain, for a real logo -- same Google favicon convention this codebase
// already uses for vendor logos (VendorRequests.tsx's VendorLogo). Falls back to the colored
// initials tile below on load failure (missing favicon, offline, etc.), never a broken image icon.
const PROVIDER_DOMAINS: Record<string, string> = {
  openai: 'openai.com', anthropic: 'anthropic.com', gemini: 'ai.google.dev', deepseek: 'deepseek.com',
  mistral: 'mistral.ai', groq: 'groq.com', xai: 'x.ai', openrouter: 'openrouter.ai',
  cohere: 'cohere.com', together_ai: 'together.ai', fireworks_ai: 'fireworks.ai',
  perplexity: 'perplexity.ai', deepinfra: 'deepinfra.com', cerebras: 'cerebras.ai',
  azure: 'azure.microsoft.com', bedrock: 'aws.amazon.com', vertex_ai: 'cloud.google.com',
  ollama: 'ollama.com',
};

function ProviderMark({ c, name, label, size = 32 }: { c: Palette; name: string; label: string; size?: number }) {
  let color = PROVIDER_COLORS[name] || c.accent;
  if (c.isDark && color === '#000000') color = '#262626';
  const initials = label.replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    || name.slice(0, 2).toUpperCase();
  const domain = PROVIDER_DOMAINS[name];
  const [failed, setFailed] = useState(false);

  if (domain && !failed) {
    return (
      <span style={{
        width: size, height: size, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: '#fff', border: `1px solid ${c.isDark ? '#2a2a2a' : c.line}`, overflow: 'hidden',
      }}>
        <img
          src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
          alt={label}
          width={size * 0.7} height={size * 0.7}
          style={{ objectFit: 'contain' }}
          onError={() => setFailed(true)}
        />
      </span>
    );
  }

  return (
    <span aria-hidden style={{
      width: size, height: size, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: color, color: '#fff', fontSize: size * 0.36, fontWeight: 700, letterSpacing: '.02em',
      border: `1px solid ${c.isDark ? '#2a2a2a' : 'transparent'}`,
    }}>{initials}</span>
  );
}

export default function AIProviders({ isDark, isAdmin, notify }: SettingsSectionProps) {
  const c = palette(isDark);

  /* ─── Server data ─────────────────────────────────────────────────────────────────────── */

  const catalogQuery = useQuery({
    queryKey: ['settings', 'ai-providers', 'catalog'],
    queryFn: async () => ((await api.get('/v1/settings/ai-providers/catalog')).data.providers || {}) as Record<string, CatalogEntry>,
    staleTime: 10 * 60_000,
  });
  // Shared with the General section's setup checklist (one cache entry for the same data).
  const providersQuery = useQuery({
    ...settingsQueries.aiProviders(),
    select: (rows: any[]) => rows as AIProviderKey[],
  });
  // Both are needed to render: if either never loaded, show the error (a failed background refetch keeps the last data).
  const loadFailed = (catalogQuery.isError && !catalogQuery.data) || (providersQuery.isError && !providersQuery.data);
  const catalog: Record<string, CatalogEntry> = (!loadFailed && catalogQuery.data) || EMPTY_CATALOG;
  const providers: AIProviderKey[] = (!loadFailed && providersQuery.data) || EMPTY_PROVIDERS;
  const loading = !loadFailed && (catalogQuery.isPending || providersQuery.isPending);

  // Typed keys, model lookups and test calls are never query keys or cached: they are mutations / on-demand calls.
  const saveMutation = useMutation({
    mutationFn: (body: ConfigBody) => api.post('/v1/settings/ai-providers', body),
    meta: { invalidates: [['settings', 'ai-providers']] },
  });
  const testMutation = useMutation({
    mutationFn: async (body: ConfigBody) => (await api.post('/v1/settings/ai-providers/test', body)).data,
  });
  // Testing an already-saved key (no api_key in the body) records last_tested_at / last_test_success on the row.
  const testSavedMutation = useMutation({
    mutationFn: async (provider_name: string) => (await api.post('/v1/settings/ai-providers/test', { provider_name })).data,
    meta: { invalidates: [['settings', 'ai-providers', 'list']] },
  });
  const toggleMutation = useMutation({
    mutationFn: (id: string) => api.put(`/v1/settings/ai-providers/${id}/toggle`),
    meta: { invalidates: [['settings', 'ai-providers']] },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/settings/ai-providers/${id}`),
    meta: { invalidates: [['settings', 'ai-providers']] },
  });

  useEffect(() => {
    if (loadFailed) console.error('Error fetching AI providers:', catalogQuery.error || providersQuery.error);
  }, [loadFailed, catalogQuery.error, providersQuery.error]);

  /* ─── Dialog state ────────────────────────────────────────────────────────────────────── */

  const [dialog, setDialog] = useState<DialogState>(null);
  const [form, setForm] = useState<{ api_key: string; default_model: string; extra_config: Record<string, string> }>({
    api_key: '', default_model: '', extra_config: {},
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [showExtraSecret, setShowExtraSecret] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AIProviderKey | null>(null);
  const [rowBusy, setRowBusy] = useState<{ id: string; action: 'test' | 'toggle' } | null>(null);

  // Live model discovery for the key currently typed in the dialog. Falls back to the catalog's curated list
  // until/unless a live fetch succeeds.
  const [liveModels, setLiveModels] = useState<string[] | null>(null);
  const [modelsFetching, setModelsFetching] = useState(false);
  const [modelsNotice, setModelsNotice] = useState<string | null>(null);
  const modelRequestSeq = useRef(0);

  const dialogProvider = dialog?.step === 'configure' ? dialog.provider : null;

  // Auto-fetch the real model list once enough of a key is typed to plausibly be valid, debounced so we don't hit
  // the provider on every keystroke. A response for an older key is ignored.
  useEffect(() => {
    const seq = ++modelRequestSeq.current;
    if (!dialogProvider || !form.api_key || form.api_key.length < 8) {
      setLiveModels(null);
      setModelsNotice(null);
      setModelsFetching(false);
      return;
    }
    const handle = setTimeout(async () => {
      setModelsFetching(true);
      setModelsNotice(null);
      try {
        const { data } = await api.post('/v1/settings/ai-providers/models', { provider_name: dialogProvider, api_key: form.api_key });
        if (seq !== modelRequestSeq.current) return;
        const models: string[] = data.models || [];
        setLiveModels(models);
        if (data.source === 'fallback') {
          setModelsNotice(data.error ? `Couldn't verify the key yet (${data.error}). Showing default models.` : 'Showing default models.');
        }
        // Keep the selected model if it's still offered; otherwise pick the first one.
        setForm(prev => ({ ...prev, default_model: models.includes(prev.default_model) ? prev.default_model : (models[0] || prev.default_model) }));
      } catch {
        if (seq !== modelRequestSeq.current) return;
        // Live fetch failed entirely (network/500): keep whatever list was already showing.
        setModelsNotice('Could not fetch the live model list. Showing default models.');
      } finally {
        if (seq === modelRequestSeq.current) setModelsFetching(false);
      }
    }, MODEL_FETCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [dialogProvider, form.api_key]);

  const openConfigure = (providerName: string, fromPicker: boolean) => {
    const entry = catalog[providerName];
    const configured = providers.find(p => p.provider_name === providerName);
    const extra_config: Record<string, string> = {};
    (entry?.extra_fields || []).forEach(f => { extra_config[f.key] = ''; });
    setForm({ api_key: '', default_model: configured?.default_model || entry?.models?.[0] || '', extra_config });
    setShowExtraSecret({});
    setShowApiKey(false);
    setTestResult(null);
    setLiveModels(null);
    setModelsNotice(null);
    setDialog({ step: 'configure', provider: providerName, fromPicker });
  };

  const closeDialog = () => {
    if (saveMutation.isPending) return;
    setDialog(null);
    setTestResult(null);
    setLiveModels(null);
    setModelsNotice(null);
  };

  // Any edit makes an earlier test result stale.
  const updateForm = (patch: Partial<typeof form>) => {
    setForm(prev => ({ ...prev, ...patch }));
    setTestResult(null);
  };

  const buildBody = (): ConfigBody | null => dialogProvider ? {
    provider_name: dialogProvider,
    api_key: form.api_key,
    default_model: form.default_model,
    extra_config: Object.keys(form.extra_config).length ? form.extra_config : null,
  } : null;

  const labelOf = (name: string) => catalog[name]?.label || name;

  const handleSave = async () => {
    const body = buildBody();
    if (!body) return;
    try {
      const { data } = await saveMutation.mutateAsync(body);
      // The server tests the saved key; only a passing test lets scans and onboarding use it.
      if (data?.verified === false) notify(`${labelOf(body.provider_name)}: ${data.message}`, 'error');
      else notify(`${labelOf(body.provider_name)} connected`, 'ok');
      setDialog(null);
      setTestResult(null);
      setLiveModels(null);
      setModelsNotice(null);
    } catch (e) {
      notify(apiError(e, 'Failed to save the provider'), 'error');
    }
  };

  const handleTest = async () => {
    const body = buildBody();
    if (!body) return;
    setTestResult(null);
    try {
      const data = await testMutation.mutateAsync(body);
      setTestResult({ success: !!data.success, message: data.message || (data.success ? 'Connection successful' : 'Connection failed') });
    } catch (e) {
      setTestResult({ success: false, message: apiError(e, 'Connection failed') });
    }
  };

  const handleTestSaved = async (row: AIProviderKey) => {
    setRowBusy({ id: row.id, action: 'test' });
    try {
      const data = await testSavedMutation.mutateAsync(row.provider_name);
      if (data.success) notify(`${labelOf(row.provider_name)}: connection successful`, 'ok');
      else notify(`${labelOf(row.provider_name)}: ${data.message || 'connection failed'}`, 'error');
    } catch (e) {
      notify(apiError(e, 'Connection test failed'), 'error');
    } finally {
      setRowBusy(null);
    }
  };

  const handleToggle = async (row: AIProviderKey) => {
    setRowBusy({ id: row.id, action: 'toggle' });
    try {
      await toggleMutation.mutateAsync(row.id);
      notify(`${labelOf(row.provider_name)} ${row.is_active ? 'paused' : 'enabled'}`, 'ok');
    } catch (e) {
      notify(apiError(e, 'Failed to change the provider status'), 'error');
    } finally {
      setRowBusy(null);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    const label = labelOf(removeTarget.provider_name);
    try {
      await deleteMutation.mutateAsync(removeTarget.id);
      notify(`${label} removed`, 'ok');
      setRemoveTarget(null);
    } catch (e) {
      notify(apiError(e, 'Failed to remove the provider'), 'error');
    }
  };

  /* ─── Render ──────────────────────────────────────────────────────────────────────────── */

  const header = (
    <SectionHeader c={c} title="AI providers"
      description="Bring your own key for the model of your choice. It powers AI summaries, indicator descriptions and company profiles; keys are encrypted at rest."
      actions={isAdmin
        ? (providers.length > 0 && !loading && !loadFailed ? <Button c={c} kind="primary" onClick={() => setDialog({ step: 'pick' })}>Add provider</Button> : undefined)
        : <ReadOnlyHint c={c}>Only admins can connect or change AI providers.</ReadOnlyHint>} />
  );

  if (loading) return <div style={{ fontFamily: sans }}>{header}<LoadingBlock c={c} rows={2} /></div>;
  if (loadFailed) {
    return (
      <div style={{ fontFamily: sans }}>
        {header}
        <ErrorBlock c={c} message={apiError(catalogQuery.error || providersQuery.error, 'The AI provider settings could not be loaded.')}
          onRetry={() => { catalogQuery.refetch(); providersQuery.refetch(); }} />
      </div>
    );
  }

  const orderedProviders = [
    ...PROVIDER_ORDER.filter(name => catalog[name]),
    ...Object.keys(catalog).filter(name => !PROVIDER_ORDER.includes(name)),
  ];
  // The list comes back newest first, and AI features use the newest enabled key (llm_client's resolver).
  const inUse = providers.find(p => !!p.is_active);
  const dialogEntry = dialogProvider ? catalog[dialogProvider] : null;
  const dialogConfigured = dialogProvider ? providers.find(p => p.provider_name === dialogProvider) : undefined;
  const keyMissing = !form.api_key && !dialogEntry?.key_optional;
  const modelOptions = liveModels ?? dialogEntry?.models ?? [];

  return (
    <div style={{ fontFamily: sans }}>
      {header}

      <div style={{ display: 'grid', gap: 16 }}>
        <Panel c={c} flush title="Connected providers"
          description={providers.length > 0
            ? 'AI features use the most recently added enabled provider. Pause the others to keep them on file without using them.'
            : undefined}
          badge={providers.length > 0 ? <Badge c={c}>{providers.length}</Badge> : undefined}>
          {providers.length === 0 ? (
            <EmptyState c={c} title="No AI provider connected"
              body={isAdmin
                ? 'Connect a provider with your own API key to turn on AI summaries, indicator descriptions and company profiles. Until then, these features use their standard non-AI text.'
                : 'An admin can connect a provider with the workspace’s own API key. Until then, AI features use their standard non-AI text.'}
              action={isAdmin ? <Button c={c} kind="primary" onClick={() => setDialog({ step: 'pick' })}>Add provider</Button> : undefined} />
          ) : (
            providers.map((row, i) => (
              <ProviderRow key={row.id} c={c} row={row} first={i === 0} label={labelOf(row.provider_name)}
                inUse={inUse?.id === row.id} isAdmin={isAdmin}
                testing={rowBusy?.id === row.id && rowBusy.action === 'test'}
                toggling={rowBusy?.id === row.id && rowBusy.action === 'toggle'}
                rowLocked={!!rowBusy}
                onTest={() => handleTestSaved(row)}
                onToggle={() => handleToggle(row)}
                onUpdate={() => openConfigure(row.provider_name, false)}
                onRemove={() => setRemoveTarget(row)} />
            ))
          )}
        </Panel>
      </div>

      {/* Step 1: pick a provider */}
      <Modal c={c} open={dialog?.step === 'pick'} onClose={closeDialog} width={640} title="Add an AI provider"
        description="Choose the provider whose API key you want to use. Connected providers can be updated with a new key or model."
        footer={<Button c={c} onClick={closeDialog}>Cancel</Button>}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
          {orderedProviders.map(name => {
            const entry = catalog[name];
            const configured = providers.some(p => p.provider_name === name);
            return (
              <PickerCard key={name} c={c} name={name} label={entry.label} configured={configured}
                onPick={() => openConfigure(name, true)} />
            );
          })}
        </div>
      </Modal>

      {/* Step 2: key -> models -> test -> save */}
      <Modal c={c} open={dialog?.step === 'configure'} onClose={closeDialog} width={560} closeOnBackdrop={false}
        title={dialogEntry ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <ProviderMark c={c} name={dialogProvider || ''} label={dialogEntry.label} size={26} />
            {dialogConfigured ? `Update ${dialogEntry.label}` : `Connect ${dialogEntry.label}`}
          </span>
        ) : 'Connect provider'}
        description={dialogConfigured
          ? 'Enter the key again to change the model, or paste a new key to replace the saved one.'
          : `Paste your ${dialogEntry?.label || ''} API key. Models available to it load automatically.`}
        footer={<>
          {dialog?.step === 'configure' && dialog.fromPicker && (
            <Button c={c} kind="ghost" style={{ marginRight: 'auto' }} disabled={saveMutation.isPending}
              onClick={() => setDialog({ step: 'pick' })}>← All providers</Button>
          )}
          <Button c={c} onClick={closeDialog} disabled={saveMutation.isPending}>Cancel</Button>
          <Button c={c} onClick={handleTest} disabled={keyMissing} busy={testMutation.isPending}>
            {testMutation.isPending ? 'Testing…' : 'Test connection'}
          </Button>
          <Button c={c} kind="primary" onClick={handleSave} disabled={keyMissing} busy={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : dialogConfigured ? 'Save changes' : 'Save provider'}
          </Button>
        </>}>
        {dialogEntry && (
          <div style={{ display: 'grid', gap: 16 }}>
            <Field c={c} label={dialogEntry.key_optional ? 'API key (optional)' : 'API key'}
              hint={dialogProvider === 'vertex_ai'
                ? 'Paste the full service-account JSON key file contents. Stored encrypted.'
                : dialogEntry.key_optional
                  ? 'Leave blank if your server has no authentication configured.'
                  : 'Stored encrypted. It is never shown again after saving.'}>
              {dialogProvider === 'vertex_ai' ? (
                <textarea value={form.api_key} rows={5} spellCheck={false} autoComplete="off"
                  placeholder={dialogConfigured ? `Currently ${dialogConfigured.api_key_masked || 'saved'}. Paste a new key file to replace it.` : (dialogEntry.key_hint || '')}
                  onChange={e => updateForm({ api_key: e.target.value })}
                  style={{ ...inputStyle(c), resize: 'vertical', lineHeight: 1.5 }} />
              ) : (
                <SecretInput c={c} value={form.api_key} shown={showApiKey} onToggleShown={() => setShowApiKey(s => !s)}
                  placeholder={dialogConfigured ? `Currently ${dialogConfigured.api_key_masked || 'saved'}. Enter a new key to replace it.` : (dialogEntry.key_hint || '')}
                  onChange={v => updateForm({ api_key: v })} />
              )}
            </Field>

            {(dialogEntry.extra_fields || []).length > 0 && (
              <div style={{ display: 'grid', gap: 16 }}>
                {(dialogEntry.extra_fields || []).map(field => (
                  <Field key={field.key} c={c} label={field.label}
                    hint={dialogConfigured ? 'Saved values are not shown; enter them again when updating.' : undefined}>
                    {field.secret ? (
                      <SecretInput c={c} value={form.extra_config[field.key] || ''} placeholder={field.placeholder}
                        shown={!!showExtraSecret[field.key]}
                        onToggleShown={() => setShowExtraSecret(p => ({ ...p, [field.key]: !p[field.key] }))}
                        onChange={v => updateForm({ extra_config: { ...form.extra_config, [field.key]: v } })} />
                    ) : (
                      <input value={form.extra_config[field.key] || ''} placeholder={field.placeholder} spellCheck={false} autoComplete="off"
                        onChange={e => updateForm({ extra_config: { ...form.extra_config, [field.key]: e.target.value } })}
                        style={inputStyle(c)} />
                    )}
                  </Field>
                ))}
              </div>
            )}

            <Field c={c} label={dialogEntry.allow_custom_model ? 'Model or deployment name' : 'Model'}
              hint={<ModelHint c={c} entry={dialogEntry} fetching={modelsFetching} liveModels={liveModels} notice={modelsNotice} />}>
              {dialogEntry.allow_custom_model || modelOptions.length === 0 ? (
                <>
                  <input value={form.default_model} list="ai-provider-model-options" spellCheck={false} autoComplete="off"
                    placeholder={dialogEntry.allow_custom_model ? 'e.g. your Azure deployment name' : 'Model name'}
                    onChange={e => updateForm({ default_model: e.target.value })} style={inputStyle(c)} />
                  <datalist id="ai-provider-model-options">
                    {modelOptions.map(m => <option key={m} value={m} />)}
                  </datalist>
                </>
              ) : (
                <select value={form.default_model} onChange={e => updateForm({ default_model: e.target.value })}
                  style={{ ...selectStyle(c), width: '100%', minWidth: 0 }}>
                  {form.default_model && !modelOptions.includes(form.default_model) && (
                    <option value={form.default_model}>{form.default_model}</option>
                  )}
                  {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              )}
            </Field>

            {testResult && (
              <Notice c={c} tone={testResult.success ? 'ok' : 'danger'}
                title={testResult.success ? 'Connection works' : 'Connection failed'}
                action={<button type="button" onClick={() => setTestResult(null)} style={btn(c, 'ghost')}>Dismiss</button>}>
                {testResult.message}
              </Notice>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog c={c} open={!!removeTarget} tone="danger" confirmLabel="Remove provider"
        title={`Remove ${removeTarget ? labelOf(removeTarget.provider_name) : 'provider'}?`}
        busy={deleteMutation.isPending}
        body={<>
          The saved key is deleted and can't be recovered. AI features switch to another enabled provider if there is one,
          otherwise they use their standard non-AI text.
        </>}
        onConfirm={handleRemove} onCancel={() => setRemoveTarget(null)} />
    </div>
  );
}

/* ─── Pieces ────────────────────────────────────────────────────────────────────────────── */

function ProviderRow({ c, row, label, first, inUse, isAdmin, testing, toggling, rowLocked, onTest, onToggle, onUpdate, onRemove }: {
  c: Palette; row: AIProviderKey; label: string; first: boolean; inUse: boolean; isAdmin: boolean; testing: boolean; toggling: boolean;
  rowLocked: boolean; onTest: () => void; onToggle: () => void; onUpdate: () => void; onRemove: () => void;
}) {
  const active = !!row.is_active;
  const tested = row.last_test_success !== null && row.last_test_success !== undefined;
  const meta: string[] = [];
  meta.push(`Model ${row.default_model || 'default'}`);
  if (row.api_key_masked) meta.push(`Key ${row.api_key_masked}`);
  meta.push(row.last_tested_at ? `Tested ${timeAgo(row.last_tested_at)}` : 'Never tested');
  meta.push(row.updated_at && row.updated_at !== row.created_at ? `Updated ${fmtDate(row.updated_at)}` : `Added ${fmtDate(row.created_at)}`);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px 16px', flexWrap: 'wrap', padding: '14px 20px',
      borderTop: first ? 'none' : `1px solid ${c.softLine}`, opacity: active ? 1 : 0.85,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 300px', minWidth: 0 }}>
        <ProviderMark c={c} name={row.provider_name} label={label} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{label}</span>
            {inUse && <Badge c={c} tone="info">In use</Badge>}
            {!active ? <Badge c={c} tone="muted" dot>Paused</Badge>
              : !tested ? <Badge c={c} tone="muted" dot>Not tested</Badge>
                : row.last_test_success ? <Badge c={c} tone="ok" dot>Working</Badge>
                  : <Badge c={c} tone="danger" dot>Last test failed</Badge>}
          </div>
          <div style={{ fontSize: 12, color: c.slate, marginTop: 3, lineHeight: 1.5, wordBreak: 'break-word' }}>
            {meta.join('  ·  ')}
          </div>
        </div>
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: c.slate, marginRight: 4 }}>
            {toggling ? <Spinner c={c} /> : null}
            <Toggle c={c} checked={active} onChange={onToggle} disabled={rowLocked} label={`${active ? 'Pause' : 'Enable'} ${label}`} />
            {active ? 'Enabled' : 'Paused'}
          </span>
          <Button c={c} onClick={onTest} busy={testing} disabled={!active || rowLocked}
            title={active ? 'Send a one-word test prompt with the saved key' : 'Enable the provider to test it'}>
            {testing ? 'Testing…' : 'Test'}
          </Button>
          <Button c={c} onClick={onUpdate} disabled={rowLocked}>Update</Button>
          <Button c={c} kind="danger" onClick={onRemove} disabled={rowLocked}>Remove</Button>
        </div>
      )}
    </div>
  );
}

function PickerCard({ c, name, label, configured, onPick }: { c: Palette; name: string; label: string; configured: boolean; onPick: () => void }) {
  const [hover, setHover] = useState(false);
  const style: CSSProperties = {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, padding: 12, borderRadius: 8, textAlign: 'left',
    border: `1px solid ${hover ? c.accent : c.line}`, background: hover ? c.hover : c.surface, cursor: 'pointer',
    fontFamily: sans, minWidth: 0, transition: 'border-color .12s, background .12s',
  };
  return (
    <button type="button" onClick={onPick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)} onBlur={() => setHover(false)} style={style}>
      <ProviderMark c={c} name={name} label={label} size={28} />
      <span style={{ fontSize: 12.5, fontWeight: 600, color: c.ink, lineHeight: 1.35, wordBreak: 'break-word' }}>{label}</span>
      {configured ? <Badge c={c} tone="ok" dot>Connected</Badge> : <span style={{ fontSize: 11.5, color: c.muted }}>Not connected</span>}
    </button>
  );
}

function SecretInput({ c, value, placeholder, shown, onToggleShown, onChange }: {
  c: Palette; value: string; placeholder?: string; shown: boolean; onToggleShown: () => void; onChange: (v: string) => void;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <input type={shown ? 'text' : 'password'} value={value} placeholder={placeholder} spellCheck={false}
        autoComplete="new-password" onChange={e => onChange(e.target.value)}
        style={{ ...inputStyle(c), paddingRight: 60 }} />
      <button type="button" onClick={onToggleShown} aria-label={shown ? 'Hide value' : 'Show value'}
        style={{ ...btn(c, 'ghost'), position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', fontSize: 12 }}>
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

function ModelHint({ c, entry, fetching, liveModels, notice }: {
  c: Palette; entry: CatalogEntry; fetching: boolean; liveModels: string[] | null; notice: string | null;
}) {
  if (fetching) {
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Spinner c={c} size={10} />Loading models available to this key…</span>;
  }
  if (entry.model_help) return <>{entry.model_help}</>;
  if (notice) return <span style={{ color: c.amber }}>{notice}</span>;
  if (liveModels) return <>{liveModels.length} model{liveModels.length === 1 ? '' : 's'} available to this key.</>;
  return <>Enter an API key above to load every model available to it.</>;
}
